import React, { useCallback, useEffect, useState } from 'react';
import { get, isNaN, orderBy } from 'lodash';
// import { Link } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
  ATMGrid,
  ATMHeader,
  ATMSegment,
  ATMField,
  ATMDatePicker,
  ATMForm,
  ATMCheckbox,
  ATMRadio,
  ATMInput,
  ATMTextArea,
  ATMDropdown,
  ATMModal,
  ATMButton,
} from 'shared-it-appmod-ui';
import { useSetting } from 'src/hooks/setting.hook';
import FieldReadOnly from 'src/components/atoms/field/field-readonly/field-readonly.component';
import Lang from 'src/libraries/language';
import { RequestStatus, RequestFlightPathName, SettingKey } from 'src/constants';
import Moment from 'src/libraries/moment.library';
// import { riskMeter } from 'src/libraries/common.library';
import { IRequestAdminFormMethods } from 'src/models/request.model';
import { useFlightOperatorContext } from 'src/contexts/flight-operator.context';
import { useFlightTypeContext } from 'src/contexts/flight-type.context';
import {
  IFlightPilot,
  IFlightTailNumber,
} from 'src/models/flight-operator.model';
import FormFlag from 'src/components/molecules/form-flag/form-flag.component';
import { getFlightOperatorStatus } from 'src/selectors/flight-operator.selector';
import { getFlightTypeStatus } from 'src/selectors/flight-type.selector';
import { actionTypes } from 'src/ducks/flight-operator.duck';
import { actionTypes as flightTypeActionTypes } from 'src/ducks/flight-type.duck';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import styles from './flight-request-admin.module.scss';
import FlightRequestMultiFlightInfo from './flight-request-multiflightinfo.component';

type IProps = IRequestAdminFormMethods;

const FlightRequestAdmin: React.FC<IProps> = ({ control, errors }) => {
  const { state, actions } = useFlightOperatorContext();
  const { state: flightTypeState, actions: flightTypeActions } =
    useFlightTypeContext();
  const FlightCategoryDrone = ['droneInternal', 'droneExternal'];
  const reason = get(control, 'defaultValuesRef.current.request.reason');
  const [pilotInCommand, setPilotInCommand] = useState<IFlightPilot[]>([]);
  const [tailNumbers, setTailNumbers] = useState<IFlightTailNumber[]>([]);
  const [flightStatus, setFlightStatus] = useState(
    control.defaultValuesRef?.current?.request?.requestStatus
  );
  const [isOpen, setIsOpen] = useState(false);

  const maxMultiFlights = useSetting(SettingKey.MaxMultiFlights, 0);

  // const riskScore = control.defaultValuesRef?.current?.request?.riskScore;
  const droneFlight =
    control.defaultValuesRef?.current?.droneFlightContractor ||
    control.defaultValuesRef?.current?.droneFlightSDGE;

  useEffect(() => {
    // Get the request flight date as default value if flight info flight date is empty
    if (!get(control, 'defaultValuesRef.current.flightInfo.flightDate')) {
      control.setValue(
        'flightInfo.flightDate',
        get(control, 'defaultValuesRef.current.request.flightDate')
      );
    }
    if (!get(control, 'defaultValuesRef.current.flightInfo.flightType')) {
      if (control.defaultValuesRef.current.passengerFlight) {
        control.setValue(
          'flightInfo.flightType',
          get(control, 'defaultValuesRef.current.passengerFlight.flightType')
        );
      }
      if (control.defaultValuesRef.current.passengerContractorFlight) {
        control.setValue(
          'flightInfo.flightType',
          get(
            control,
            'defaultValuesRef.current.passengerContractorFlight.flightType'
          )
        );
      }
    }
  }, [control, flightTypeState]);

  // useEffect(() => {
  //   return () => {
  //     window.location.reload();
  //   };
  // }, [riskScore]);

  const handleFlightStatus = useCallback(
    (value) => {
      control.setValue('request.requestStatus', value);

      setFlightStatus(value);
    },
    [control, setFlightStatus]
  );

  useEffect(() => {
    actions.listGET(true);
    flightTypeActions.listGET();
  }, [actions, flightTypeActions]);

  useEffect(() => {
    const value = get(
      control,
      'defaultValuesRef.current.flightInfo.operatorId'
    );

    if (value && state.list.length) {
      const list = state.list.find((item) => value === item.operatorId);
      setPilotInCommand(orderBy(list?.Pilots ?? [], ['name'], ['asc']));
      setTailNumbers(orderBy(list?.TailNumbers ?? [], ['tailNumber'], ['asc']));
    }
  }, [state.list, control, setPilotInCommand, setTailNumbers]);

  const handleChange = useCallback(
    (value) => {
      const operator = state.list.find((item) => value === item.name);

      setPilotInCommand(orderBy(operator?.Pilots ?? [], ['name'], ['asc']));
      setTailNumbers(
        orderBy(operator?.TailNumbers ?? [], ['tailNumber'], ['asc'])
      );
      control.setValue('flightInfo.operatorId', operator?.operatorId);
      control.setValue('flightInfo.pilot', undefined);
      control.setValue('flightInfo.tailNumber', undefined);
    },
    [state, control, setPilotInCommand, setTailNumbers]
  );

  const status = getFlightOperatorStatus(
    state,
    actionTypes.FLIGHT_OPERATOR_FETCH
  );

  const flightTypeStatus = getFlightTypeStatus(
    flightTypeState,
    flightTypeActionTypes.FLIGHT_TYPE_FETCH
  );

  return (
    <>
      <ATMSegment id="admin-section-form" color="blue">
        <ATMHeader>{Lang.TTL_AVIATION_SERVICE}</ATMHeader>
        <p>{Lang.NTE_AVIATION_SERVICE_ADMIN}</p>

        <ATMSegment className={styles.wrapper}>
          <ATMHeader size="small" className="text-underline">
            {Lang.TTL_FLIGHT_DATE_TIME}
          </ATMHeader>

          <ATMGrid columns={4}>
            <ATMGrid.Column>
              <ATMField
                name="flightInfo.flightDate"
                format="MM/DD/YYYY"
                as={ATMInput}
                control={control}
                className="hidden"
              />
              <ATMField
                name="request.flightDate"
                label={
                  <LabelRequired>{Lang.LBL_REQUEST_FLIGHT_DATE}</LabelRequired>
                }
                format="MM/DD/YYYY"
                className={styles.datePicker}
                as={ATMDatePicker}
                control={control}
                error={errors.request?.flightDate}
                onChange={([_, { value }]) => {
                  control.setValue('flightInfo.flightDate', value);

                  return value;
                }}
                minDate={Moment().startOf('day').toDate()}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <ATMField
                name="flightInfo.departureTime"
                label={Lang.LBL_REQUEST_FLIGHT_DEPARTURE}
                as={ATMInput}
                type="number"
                placeholder={Lang.LBL_ENTER}
                control={control}
                error={errors.flightInfo?.departureTime}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <ATMField
                name="flightInfo.arrivalTime"
                label={Lang.LBL_REQUEST_FLIGHT_ARRIVAL}
                as={ATMInput}
                type="number"
                placeholder={Lang.LBL_ENTER}
                control={control}
                error={errors.flightInfo?.arrivalTime}
              />
            </ATMGrid.Column>
            {!droneFlight && (
              <>
                <ATMGrid.Column>
                  <FieldReadOnly label={Lang.LBL_REQUEST_FLIGHT_HOURS}>
                    <ATMField
                      name="flightInfo.flightHours"
                      as={ATMInput}
                      type="number"
                      control={control}
                      placeholder={Lang.LBL_ENTER}
                      error={errors.flightInfo?.flightHours}
                    />
                  </FieldReadOnly>
                </ATMGrid.Column>
                <ATMGrid.Column>
                  <ATMField
                    name="flightInfo.standByHours"
                    label={Lang.LBL_REQUEST_FLIGHT_STANDBY_HOURS}
                    type="number"
                    as={ATMInput}
                    placeholder={Lang.LBL_ENTER}
                    control={control}
                    error={errors.flightInfo?.standByHours}
                  />
                </ATMGrid.Column>
              </>
            )}
            <ATMGrid.Column>
              <ATMField
                name="flightInfo.flightNo"
                label={Lang.LBL_FLIGHT_NO}
                as={ATMInput}
                control={control}
                readOnly
              />
            </ATMGrid.Column>
          </ATMGrid>

          <ATMHeader size="small" className="text-underline">
            {Lang.TTL_DETAILS}
          </ATMHeader>

          <ATMGrid columns={4}>
            <ATMGrid.Column>
              <ATMField
                key={`flight_type_${flightTypeState.list.length}`}
                name="flightInfo.flightType"
                label={Lang.LBL_FLIGHT_TYPE}
                as={ATMDropdown}
                placeholder={Lang.LBL_SELECT}
                control={control}
                options={flightTypeState.list.map((value) => ({
                  key: value.flightTypeId,
                  value: value.flightType,
                  text: value.flightType,
                }))}
                search
                error={errors.flightInfo?.flightType}
                onChange={([_, { value }]) => {
                  if (control.defaultValuesRef?.current?.passengerFlight) {
                    control.setValue('passengerFlight.flightType', value);
                  }
                  if (
                    control.defaultValuesRef?.current?.passengerContractorFlight
                  ) {
                    control.setValue(
                      'passengerContractorFlight.flightType',
                      value
                    );
                  }
                  return value;
                }}
                loading={
                  !flightTypeState.list.length && flightTypeStatus.fetching
                }
                disabled={
                  !flightTypeState.list.length && flightTypeStatus.fetching
                }
                selectOnBlur={false}
                selection
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <ATMField
                name="flightInfo.operatorId"
                as={ATMInput}
                control={control}
                type="hidden"
                className="hidden"
              />

              <ATMField
                name="flightInfo.operatorName"
                label={Lang.LBL_OPERATOR}
                placeholder={Lang.LBL_SELECT}
                as={ATMDropdown}
                control={control}
                defaultValue={
                  control.defaultValuesRef?.current?.flightInfo?.operatorName ??
                  control.defaultValuesRef?.current
                    ?.constructionContractorFlight?.operatorName
                }
                options={state.list.map((item) => ({
                  key: item.operatorId,
                  value: item.name,
                  text: item.name,
                }))}
                onChange={([_, { value }]) => {
                  handleChange(value);

                  return value;
                }}
                search
                error={errors.flightInfo?.operatorName}
                loading={!state.list.length && status.fetching}
                disabled={!state.list.length && status.fetching}
                selectOnBlur={false}
                selection
                clearable
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <ATMField
                key={`pilot_${state.list.length}`}
                name="flightInfo.pilot"
                label={Lang.LBL_PILOT_IN_COMMAND}
                placeholder={Lang.LBL_SELECT}
                as={ATMDropdown}
                options={pilotInCommand.map((item) => ({
                  key: item.pilotId,
                  value: item.name,
                  text: item.name,
                }))}
                control={control}
                onChange={([_, { value }]) => value}
                error={errors.flightInfo?.pilot}
                loading={!state.list.length && status.fetching}
                selectOnBlur={false}
                selection
                clearable
                search
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <ATMField
                key={`tailnumber_${state.list.length}`}
                name="flightInfo.tailNumber"
                label={Lang.LBL_TAIL_NUMBER}
                as={ATMDropdown}
                options={tailNumbers.map((item) => ({
                  key: item.tailId,
                  value: item.tailNumber,
                  text: item.tailNumber,
                }))}
                placeholder={Lang.LBL_SELECT}
                control={control}
                onChange={([_, { value }]) => value}
                error={errors.flightInfo?.tailNumber}
                loading={!state.list.length && status.fetching}
                selectOnBlur={false}
                selection
                clearable
                search
              />
            </ATMGrid.Column>
            {!droneFlight && (
              <>
                <ATMGrid.Column>
                  <ATMField
                    name="flightInfo.landingAssured"
                    label={Lang.LBL_LANDING_ASSURED}
                    as={ATMInput}
                    placeholder={Lang.LBL_ENTER}
                    control={control}
                    error={errors.flightInfo?.landingAssured}
                    maxLength={4}
                    onChange={([_, { value }]) => {
                      let temp = value.replace(/[^0-9]/, '');
                      if (temp === 2400) {
                        temp = '0000';
                      } else if (temp > 2359 && temp !== 2400) {
                        temp = 2359;
                      } else if (temp > 999 && temp % 100 > 60) {
                        return parseInt(
                          `${Math.floor(temp / 100)}${(temp % 100) - 60}`,
                          10
                        );
                      }

                      return temp;
                    }}
                    onBlur={(_, value) => {
                      const temp = value ? parseInt(value, 10) : 0;
                      /* eslint no-else-return: "error" */
                      if (temp === 2400 || temp === 0) {
                        return '0000';
                      } else if (temp > 2359 && temp !== 2400) {
                        return 2359;
                      } else if (temp > 999 && temp % 100 > 60) {
                        return `${Math.floor(temp / 100) + 1}${
                          (temp % 100) - 60
                        }`;
                      } else if (temp > 999 && temp % 100 === 60) {
                        return `${Math.floor(temp / 100) + 1}00`;
                      } else if (temp > 999) {
                        return temp;
                      } else if (temp > 99 && temp % 100 > 60) {
                        return Math.floor(temp / 100) + 1 === 10
                          ? `${Math.floor(temp / 100) + 1}${(temp % 100) - 60}`
                          : `0${Math.floor(temp / 100) + 1}${
                              (temp % 100) - 60
                            }`;
                      } else if (temp > 99 && temp % 100 === 60) {
                        return `0${Math.floor(temp / 100) + 1}00`;
                      } else if (temp > 99) {
                        return `0${temp}`;
                      } else if (temp < 100 && temp > 60) {
                        return `00${(temp % 100) - 60}`;
                      } else if (temp === 60 || temp === 100) {
                        return '0100';
                      } else if (temp < 60 && temp > 9) {
                        return `00${temp}`;
                      } else if (temp < 10) {
                        return `000${temp}`;
                      }

                      return temp;
                    }}
                  />
                </ATMGrid.Column>

                <ATMGrid.Column>
                  <ATMField
                    name="flightInfo.flightCost"
                    label={Lang.LBL_FLIGHT_COST}
                    as={ATMInput}
                    placeholder={Lang.LBL_ENTER}
                    control={control}
                    onBlur={(_, value) => {
                      if (value) {
                        const temp = value
                          .replace('$', '')
                          .replace(',', '')
                          .replace(/[^0-9.]+/, '');

                        const amount = parseFloat(temp);

                        return !isNaN(amount)
                          ? `$${parseFloat(temp)
                              .toFixed(2)
                              .replace(/\d(?=(\d{3})+\.)/g, '$&,')}`
                          : '';
                      }
                      return '';
                    }}
                  />
                </ATMGrid.Column>
              </>
            )}
            <ATMGrid.Column>
              {
                !FlightCategoryDrone.includes(
                  RequestFlightPathName[
                    get(
                      control,
                      'defaultValuesRef.current.request.flightFlag'
                    ) ?? 1
                  ]
                )
                // && (
                //   <div className={styles.score}>
                //     <ATMField
                //       name="request.riskScore"
                //       defaultValue={riskScore}
                //       label={Lang.LBL_RISK_ASSESSMENT_SCORE}
                //       as={ATMInput}
                //       type="number"
                //       control={control}
                //     />
                //     {riskScore &&
                //     riskScore !== '0' &&
                //     riskScore !== 'undefined' ? (
                //       <span>
                //         <i className={riskMeter(Number(riskScore))} />
                //       </span>
                //     ) : null}
                //     <Link
                //       to={`/flight/${control.defaultValuesRef.current.request?.requestId}/assessment`}
                //       target="_self"
                //     >
                //       {Lang.LBL_GOTO_RISK_ASSESSMENT}
                //     </Link>
                //   </div>
                // )
              }
            </ATMGrid.Column>
          </ATMGrid>
          {control.defaultValuesRef.current?.flightInfo != null &&
            control.defaultValuesRef.current?.flightInfo?.flightNo !== null && (
              <ATMGrid columns={1}>
                <ATMGrid.Column>
                  <ATMSegment>
                    <FlightRequestMultiFlightInfo
                      name="multiFlightInfo"
                      control={control}
                      errors={errors}
                      limit={maxMultiFlights}
                    />
                  </ATMSegment>
                </ATMGrid.Column>
              </ATMGrid>
            )}
          {droneFlight !== null && (
            <ATMGrid columns={1}>
              <ATMGrid.Column>
                <FormFlag
                  name="flightInfo.addFaaWaiverAuth"
                  label={Lang.LBL_ADD_FAA_WAIVER_AUTH}
                  control={control}
                  errors={errors}
                />
              </ATMGrid.Column>
            </ATMGrid>
          )}
          <ATMGrid columns={1}>
            <ATMGrid.Column>
              <FormFlag
                name="flightInfo.noiseMonitoring"
                label={Lang.LBL_NOISE_MONITORING}
                control={control}
                errors={errors}
              />
            </ATMGrid.Column>
          </ATMGrid>

          <ATMGrid columns={1}>
            <ATMGrid.Column>
              <ATMField
                name="flightInfo.faaInformation"
                label={Lang.LBL_FAA_INFORMATION}
                as={ATMTextArea}
                placeholder={Lang.LBL_ENTER}
                control={control}
                error={errors.flightInfo?.faaInformation}
              />
            </ATMGrid.Column>
          </ATMGrid>

          <ATMHeader size="small" className="text-underline">
            {Lang.TTL_CHECKLIST}
          </ATMHeader>

          <ATMGrid columns={1}>
            {!droneFlight && (
              <>
                <ATMGrid.Column>
                  <Controller
                    name="flightInfo.observedFlag"
                    control={control}
                    render={({ onChange, value }) => (
                      <ATMCheckbox
                        label={Lang.LBL_OBSERVED}
                        onChange={(_, { checked }) => onChange(checked)}
                        checked={value}
                      />
                    )}
                  />
                </ATMGrid.Column>
                <ATMGrid.Column>
                  <Controller
                    name="flightInfo.contractorMissionFlag"
                    control={control}
                    render={({ onChange, value }) => (
                      <ATMCheckbox
                        label={Lang.LBL_CONTRACTOR_MISSION}
                        onChange={(_, { checked }) => onChange(checked)}
                        checked={value}
                      />
                    )}
                  />
                </ATMGrid.Column>
              </>
            )}
            <ATMGrid.Column>
              <Controller
                name="flightInfo.longRifleApproval"
                control={control}
                render={({ onChange, value }) => (
                  <ATMCheckbox
                    label={Lang.LBL_MCBCP_LONG_RIFLE_APPROVAL}
                    onChange={(_, { checked }) => onChange(checked)}
                    checked={value}
                  />
                )}
              />
            </ATMGrid.Column>
          </ATMGrid>
        </ATMSegment>

        <ATMSegment className={styles.wrapper}>
          <ATMHeader size="small" className="text-underline">
            {Lang.TTL_FLIGHT_REQUEST_STATUS}
          </ATMHeader>

          <ATMGrid columns={1}>
            <ATMGrid.Column>
              <ATMForm.Field error={!!errors.request?.requestStatus} inline>
                <LabelRequired>{Lang.LBL_STATUS}</LabelRequired>

                <ATMField
                  className="hidden"
                  as={ATMInput}
                  control={control}
                  type="hidden"
                  name="request.requestStatus"
                  defaultValue={flightStatus}
                />
                <ATMForm.Group grouped>
                  {[
                    RequestStatus.Approved,
                    RequestStatus.PendingApproval,
                    RequestStatus.Declined,
                    RequestStatus.InFlight,
                    RequestStatus.Completed,
                    RequestStatus.RequiresFlightCost,
                    RequestStatus.Cancelled,
                  ].map((v) => (
                    <ATMForm.Field key={v}>
                      <ATMModal
                        open={isOpen}
                        size="small"
                        trigger={
                          <ATMRadio
                            label={Lang.REQUEST_STATUS[v]}
                            value={v}
                            checked={flightStatus === v}
                            onChange={() => {
                              if (
                                v === RequestStatus.Completed &&
                                !get(
                                  control,
                                  'defaultValuesRef.current.flightInfo.flightNo'
                                )
                              ) {
                                setIsOpen(true);
                              } else {
                                handleFlightStatus(v);
                              }
                            }}
                          />
                        }
                        header={Lang.TTL_CONFIRM_STATUS_CHANGE}
                        content={
                          <div className={styles.modalContent}>
                            {Lang.NTE_REQUEST_STATUS_CHANGE_1}
                            <br />
                            <br />
                            <b>{Lang.NTE_REQUEST_STATUS_CHANGE_2}</b>
                          </div>
                        }
                        actions={
                          <div className={styles.actionBtns}>
                            <ATMButton
                              basic
                              color="blue"
                              content={Lang.LBL_NO}
                              onClick={() => setIsOpen(false)}
                            />
                            <ATMButton
                              primary
                              content={Lang.LBL_YES}
                              onClick={() => {
                                handleFlightStatus(RequestStatus.Completed);
                                setIsOpen(false);
                              }}
                            />
                          </div>
                        }
                      />
                    </ATMForm.Field>
                  ))}
                </ATMForm.Group>
              </ATMForm.Field>
            </ATMGrid.Column>

            {reason && (
              <ATMGrid.Column>
                <FieldReadOnly label={Lang.LBL_REASON_COMMENTS}>
                  {reason}
                </FieldReadOnly>
              </ATMGrid.Column>
            )}

            <ATMGrid.Column>
              <ATMField
                name="flightInfo.adminComments"
                label={Lang.LBL_NOTES}
                as={ATMTextArea}
                placeholder={Lang.LBL_ENTER}
                control={control}
                error={errors.flightInfo?.adminComments}
              />
            </ATMGrid.Column>
          </ATMGrid>
        </ATMSegment>
      </ATMSegment>
    </>
  );
};

export default FlightRequestAdmin;
